import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { listWorkflows } from '../services/workflowService';
import { Workflow, WorkflowStatus } from '../types/workflow';

const STATUS_STYLES: Record<WorkflowStatus, { bg: string; color: string; label: string }> = {
  healthy:  { bg: '#E9F6F6', color: '#0F6E56', label: 'Healthy' },
  degraded: { bg: '#FEF3C7', color: '#92400e', label: 'Degraded' },
  paused:   { bg: '#F5F7FA', color: '#595A60', label: 'Paused' },
  failing:  { bg: '#FEE2E2', color: '#b91c1c', label: 'Failing' },
};

const CRITICALITY_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  'tier-1': { bg: '#FEE2E2', color: '#b91c1c', label: 'Tier 1' },
  'tier-2': { bg: '#FEF3C7', color: '#92400e', label: 'Tier 2' },
  'tier-3': { bg: '#F5F7FA', color: '#595A60', label: 'Tier 3' },
};

const STATUS_FILTERS = ['All', 'Healthy', 'Degraded', 'Paused', 'Failing'] as const;
const CATEGORY_FILTERS = ['All', 'Fulfillment', 'Corporate Actions'] as const;

type StatusFilter = typeof STATUS_FILTERS[number];
type CategoryFilter = typeof CATEGORY_FILTERS[number];

function fmtPct(n: number) {
  return `${n.toFixed(1)}%`;
}

function fmtDuration(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 100) / 10;
  return s < 60 ? `${s}s` : `${(s / 60).toFixed(1)}m`;
}

function fmtRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function WorkflowLibraryPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('All');
  const [aiOnly, setAiOnly] = useState(false);

  useEffect(() => {
    setTopbar('Workflows', 'Enterprise workflow library — search, monitor, and inspect');
  }, [setTopbar]);

  const workflows: Workflow[] = listWorkflows();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workflows.filter(w => {
      if (statusFilter !== 'All' && STATUS_STYLES[w.status].label !== statusFilter) return false;
      if (categoryFilter !== 'All' && w.category !== categoryFilter) return false;
      if (aiOnly && !w.ai_enabled) return false;
      if (!q) return true;
      return (
        w.name.toLowerCase().includes(q) ||
        w.description.toLowerCase().includes(q) ||
        w.owner.toLowerCase().includes(q) ||
        w.tags.some(t => t.toLowerCase().includes(q)) ||
        w.connections.some(c => c.name.toLowerCase().includes(q)) ||
        w.logic.executive_summary.toLowerCase().includes(q)
      );
    });
  }, [workflows, search, statusFilter, categoryFilter, aiOnly]);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-wash">
      {/* Summary header */}
      <div className="px-8 pt-6 pb-5 flex-shrink-0 bg-white border-b border-trim">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink">Workflow Library</h1>
            <p className="text-xs text-muted mt-0.5">All enterprise workflows across Fulfillment and Corporate Actions</p>
          </div>
          <div className="flex items-center gap-6">
            {[
              { label: 'Total',    val: workflows.length,                           cls: 'text-ink' },
              { label: 'Healthy',  val: workflows.filter(w => w.status === 'healthy').length,  cls: 'text-emerald-600' },
              { label: 'Degraded', val: workflows.filter(w => w.status === 'degraded').length, cls: 'text-amber-600' },
              { label: 'Failing',  val: workflows.filter(w => w.status === 'failing').length,  cls: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className={`text-2xl font-bold leading-none ${s.cls}`}>{s.val}</div>
                <div className="text-xs text-muted mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-trim flex-shrink-0 bg-white flex-wrap">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>
          {filtered.length} workflows
        </div>

        <div className="flex gap-1.5">
          {STATUS_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
              style={{
                background: statusFilter === f ? '#14b8a6' : '#F5F7FA',
                color: statusFilter === f ? 'white' : '#595A60',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex gap-1.5">
          {CATEGORY_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setCategoryFilter(f)}
              className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
              style={{
                background: categoryFilter === f ? '#063F5A' : '#F5F7FA',
                color: categoryFilter === f ? 'white' : '#595A60',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-xs text-copy cursor-pointer">
          <input
            type="checkbox"
            checked={aiOnly}
            onChange={e => setAiOnly(e.target.checked)}
            className="accent-teal-500"
          />
          AI-enabled only
        </label>

        <div className="ml-auto relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M9.5 9.5l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search workflows, tags, owners, integrations…"
            className="pl-8 pr-3 py-1.5 rounded-lg border border-trim text-sm outline-none focus:border-teal-400 transition-colors bg-white"
            style={{ width: 320 }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="bg-white rounded-xl border border-trim overflow-hidden shadow-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-trim bg-wash">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Workflow</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Owner</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Env</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Tier</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Runs 24h</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Success</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Avg dur</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Integrations</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">AI</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted">Last run</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-sm text-[#A0A3AB]">No workflows match the filters</td>
                </tr>
              ) : (
                filtered.map(w => {
                  const st = STATUS_STYLES[w.status];
                  const cr = CRITICALITY_STYLES[w.criticality];
                  return (
                    <tr
                      key={w.id}
                      onClick={() => navigate(`/workflows/${w.id}`)}
                      className="border-b border-wash last:border-0 hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink">{w.name}</div>
                        <div className="text-xs text-muted mt-0.5 max-w-[420px] truncate">{w.description}</div>
                      </td>
                      <td className="px-4 py-3 text-copy text-xs">{w.category}</td>
                      <td className="px-4 py-3 text-copy text-xs">{w.owner}</td>
                      <td className="px-4 py-3 text-muted text-xs uppercase">{w.environment}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: cr.bg, color: cr.color }}>
                          {cr.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-copy font-mono text-xs">{w.executions_24h}</td>
                      <td className="px-4 py-3 text-right text-copy font-mono text-xs">{fmtPct(w.success_rate)}</td>
                      <td className="px-4 py-3 text-right text-copy font-mono text-xs">{fmtDuration(w.avg_duration_ms)}</td>
                      <td className="px-4 py-3 text-right text-copy font-mono text-xs">{w.integrations_count}</td>
                      <td className="px-4 py-3 text-xs">
                        {w.ai_enabled ? (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: '#EEF2FF', color: '#4338ca' }}>AI</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3 text-muted text-xs">{fmtRelative(w.last_run_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
